import { useEffect, useMemo, useState } from "react";
import { Input, Select, Modal, Segmented, Tag, Typography, Divider, Checkbox, Space, message } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import DeleteConfirmationModal from "../components/common/DeleteConfirmationModal";
import TextInput from "../components/common/TextInput";
import { UNIT_MAP, formatUnit, RESOURCE_CODE_OPERATOR_OPTIONS } from "../utils/constants";
import {
  computeDynamicBillSummaryNodeAmount,
  computeDynamicBillSummaryNodeRate,
  isDynamicBillSummarySectionNode,
  mapDynamicBillSummaryNodes,
  removeDynamicBillSummaryNodeById,
} from "../utils/dynamicBillSummarySerialization";
import {
  getPlotMatrixStageNames,
  findPlotMatrixColumnsForStage,
  getPlotMatrixSubStageLabel,
  isGarageEligibleValuationStage,
} from "../utils/dynamicBillSummaryPlotMatrix";
import {
  getRunMatrixMappingSectionLabels,
  findRunMatrixMappingColumnsForSection,
  type RunMatrixMappingSectionKey,
} from "../utils/dynamicBillSummaryRunMatrix";
import type {
  DynamicBillSummaryNode,
  DynamicBillSummaryItemNode,
  DynamicBillSummarySectionNode,
  DynamicBillSummaryResourceCodeEntry,
  DynamicBillSummaryPlotMapping,
  DynamicBillSummaryRunMatrixMapping,
} from "../types/tender.types";
import type { ItemLibraryItem } from "../api/itemLibrary";
import type { PlotMatrixCategoryColumn } from "../utils/plotMatrixCategoryGenerator";

const { Text } = Typography;

let idCounter = 0;
const genExtraNodeId = () => `extra${Date.now()}_${idCounter++}`;

/** One converted row, ready to graft into a fixed Bill Summary's own `any[]`
 *  processedData tree — same field names/shapes (`category`/`description`/
 *  `quantity`/`unit`/`rate`/`amount`/`rowType`/`children`/`sectionAmount`) every
 *  fixed summary table's columns already render, so no column render function
 *  needs an extra-row-specific branch beyond checking `isExtra` for the
 *  Actions column. */
export interface BillSummaryExtraRow {
  key: string;
  category: string;
  description: string;
  rowType: "main-header" | "column-type-header" | "line-item";
  isExtra: true;
  attachKey: string;
  nodeType: "section" | "item";
  quantity: string;
  unit: string;
  rate: string;
  amount: string;
  amountText?: string;
  amountType?: "number" | "text";
  resourceCode?: string;
  resourceCodes?: DynamicBillSummaryResourceCodeEntry[];
  dbRate: string;
  sectionAmount?: number;
  children?: BillSummaryExtraRow[];
}

const buildExtraRow = (
  node: DynamicBillSummaryNode,
  category: string,
  childPrefix: string,
  attachKey: string,
  depth: number,
  markupFactor: number,
): BillSummaryExtraRow => {
  if (isDynamicBillSummarySectionNode(node)) {
    const children = node.children.map((child, i) =>
      buildExtraRow(child, `${childPrefix}.${i + 1}`, `${childPrefix}.${i + 1}`, attachKey, depth + 1, markupFactor),
    );
    const sectionAmount = children.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0);
    return {
      key: node.id,
      category,
      description: node.label,
      rowType: depth === 0 ? "main-header" : "column-type-header",
      isExtra: true,
      attachKey,
      nodeType: "section",
      quantity: "",
      unit: "",
      rate: "",
      amount: "",
      dbRate: "",
      sectionAmount,
      children,
    };
  }
  // Rate/Amount shown marked up — same OH&P/Preliminaries treatment every
  // fixed Bill Summary's own Rate/Amount columns already get. `dbRate` stays
  // raw: it's the backend-style dbRate-only figure the dev-mode SummaryKey
  // column compares against the persisted (also-raw) line, so marking it up
  // here would show a false mismatch.
  const amount = computeDynamicBillSummaryNodeAmount(node, markupFactor);
  const markedUpRate = node.rate != null ? computeDynamicBillSummaryNodeRate(node.rate, markupFactor) : undefined;
  return {
    key: node.id,
    category,
    description: node.description,
    rowType: "line-item",
    isExtra: true,
    attachKey,
    nodeType: "item",
    quantity: node.qty != null ? String(node.qty) : "",
    unit: node.unit || "",
    rate: markedUpRate != null ? markedUpRate.toFixed(2) : "",
    amount: node.amountType === "text" ? "" : amount ? amount.toFixed(2) : "",
    amountText: node.amountText,
    amountType: node.amountType,
    resourceCode: node.resourceCode,
    resourceCodes: node.resourceCodes,
    dbRate: node.rate != null ? node.rate.toFixed(4) : "",
  };
};

/** Converts a root-bucket's top-level extra nodes (`extraItems["__root__"]`) into rows —
 *  each gets its own whole top-level number continuing on from the table's own last
 *  main-header number (e.g. last real section is "3.0" → first extra root row is "4.0").
 *  `markupFactor` (default 1, i.e. no-op) applies OH&P/Preliminaries the same way the
 *  fixed rows around these extra rows already have it applied — pass the caller's own
 *  live `(1 + overheadAndProfit/100) * (1 + preliminaries/100)`. */
export const buildRootExtraRows = (
  nodes: DynamicBillSummaryNode[],
  attachKey: string,
  startNumber: number,
  markupFactor: number = 1,
): BillSummaryExtraRow[] =>
  nodes.map((node, i) => {
    const n = startNumber + i;
    return buildExtraRow(node, `${n}.0`, `${n}`, attachKey, 0, markupFactor);
  });

/** Converts a bucket of extra nodes grafted onto an existing row (`extraItems[row.key]`)
 *  into rows, numbered as a continuation of that row's own existing children
 *  (e.g. a row already showing children "3.1.1" gets its first extra child "3.1.2").
 *  `markupFactor` — see `buildRootExtraRows` above. */
export const buildNestedExtraRows = (
  nodes: DynamicBillSummaryNode[],
  attachKey: string,
  parentCategory: string,
  existingSiblingCount: number,
  markupFactor: number = 1,
): BillSummaryExtraRow[] => {
  // A top-level section's own category carries a trailing ".0" (e.g. "3.0" —
  // see the fixed table's own `itemNumber` and buildRootExtraRows below), so
  // appending a child index onto the raw string reads "3.0.1" instead of the
  // expected "3.1". Strip it so a child continues the section's own number
  // directly; a category that's already nested (e.g. "3.1", no ".0" suffix)
  // is unaffected.
  const base = parentCategory.endsWith(".0") ? parentCategory.slice(0, -2) : parentCategory;
  return nodes.map((node, i) => {
    const category = `${base}.${existingSiblingCount + i + 1}`;
    return buildExtraRow(node, category, category, attachKey, 1, markupFactor);
  });
};

type ModalState =
  | { kind: "item"; attachKey: string; parentNodeId: string | null; editingNodeId: string | null }
  | { kind: "section"; attachKey: string; parentNodeId: string | null; editingNodeId: string | null }
  | null;

export interface UseBillSummaryExtraItemsOptions {
  /** Current `Record<attachKey, DynamicBillSummaryNode[]>` — e.g. `state.groundworks.cutFillsExtraItems`. */
  extraItems: Record<string, DynamicBillSummaryNode[]> | undefined;
  onChange: (updated: Record<string, DynamicBillSummaryNode[]>) => void;
  itemLibraryData?: ItemLibraryItem[];
  /** Live Plot Matrix columns (`generatePlotMatrixCategoryColumns` on the current
   *  groundworks data) — drives the Add/Edit Item modal's "Include in Plot
   *  Matrix" Valuation Stage / Stage lookups, same as DynamicBillSummaryPanel's
   *  own `plotMatrixColumns` prop. Omit to hide that section entirely. */
  plotMatrixColumns?: PlotMatrixCategoryColumn[];
  /** Whether to offer the "Run Matrix Mapping" section — the Roads & Sewers
   *  counterpart of `plotMatrixColumns` above. Unlike Plot Matrix's stages,
   *  Run Matrix's section/column catalog is static (see
   *  RUN_MATRIX_MAPPING_SECTIONS in dynamicBillSummaryRunMatrix.ts), so this
   *  is just an on/off flag rather than a live-derived list. Omit (or leave
   *  false) to hide that section entirely — e.g. on /groundworks/review, which
   *  has Plot Matrix instead. */
  enableRunMatrixMapping?: boolean;
  /** Fired right after a brand-new Section or Item is added (not on Edit) —
   *  `expandKey` is the existing row's own key that needs expanding to reveal
   *  the new node (the row it was grafted onto — `null` for a new top-level
   *  root row, nothing to expand), `nodeId` is the new node's own id. The
   *  host table owns `expandedRowKeys` (this hook doesn't), so it uses this
   *  to auto-expand `expandKey`'s row and flash-highlight/autofocus-Qty the
   *  new row — same convention DynamicBillSummaryPanel.tsx follows internally. */
  onNodeAdded?: (expandKey: string | null, nodeId: string) => void;
}

/**
 * Reusable ad-hoc Section/Item CRUD for a fixed (computed, otherwise-immutable)
 * Bill Summary table — lets a user graft extra rows onto specific existing rows
 * (or the table root) without touching the computed data those rows already show.
 * Mirrors `useDynamicBillSummary`/`DynamicBillSummaryPanel`'s own node-tree model
 * and Add/Edit Item modal (single/multiple item-library codes, Number/Text amount
 * type, optional Plot Matrix mapping).
 */
export function useBillSummaryExtraItems({
  extraItems,
  onChange,
  itemLibraryData,
  plotMatrixColumns = [],
  enableRunMatrixMapping = false,
  onNodeAdded,
}: UseBillSummaryExtraItemsOptions) {
  const buckets = extraItems ?? {};

  const getBucket = (attachKey: string): DynamicBillSummaryNode[] => buckets[attachKey] ?? [];

  const setBucket = (attachKey: string, nodes: DynamicBillSummaryNode[]) => {
    const next = { ...buckets };
    if (nodes.length === 0) {
      delete next[attachKey];
    } else {
      next[attachKey] = nodes;
    }
    onChange(next);
  };

  const addNode = (attachKey: string, parentNodeId: string | null, newNode: DynamicBillSummaryNode) => {
    const bucket = getBucket(attachKey);
    if (parentNodeId === null) {
      setBucket(attachKey, [...bucket, newNode]);
      return;
    }
    setBucket(
      attachKey,
      mapDynamicBillSummaryNodes(bucket, parentNodeId, (node) =>
        isDynamicBillSummarySectionNode(node) ? { ...node, children: [...node.children, newNode] } : node,
      ),
    );
  };

  const updateNode = (attachKey: string, nodeId: string, patch: Partial<DynamicBillSummaryNode>) => {
    setBucket(
      attachKey,
      mapDynamicBillSummaryNodes(getBucket(attachKey), nodeId, (node) => ({ ...node, ...patch }) as DynamicBillSummaryNode),
    );
  };

  const removeNode = (attachKey: string, nodeId: string) => {
    setBucket(attachKey, removeDynamicBillSummaryNodeById(getBucket(attachKey), nodeId));
  };

  const [modalState, setModalState] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ attachKey: string; nodeId: string } | null>(null);

  // Section modal — label only.
  const [sectionLabel, setSectionLabel] = useState("");

  // Item modal — same field set as DynamicBillSummaryPanel's Add/Edit Item modal.
  // Qty has no field here — same as Dynamic Bill Summary, it's entered/edited
  // directly in the host table's Qty cell (GroundworksCutFillBillSummary.tsx's
  // inline NumberInput for `record.isExtra` rows) rather than in this modal.
  // `qty` is still tracked here so editing a row's other fields via this modal
  // doesn't clobber whatever Qty was already set inline.
  const [description, setDescription] = useState("");
  const [qty, setQty] = useState<number | undefined>(undefined);
  const [unit, setUnit] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedCode, setSelectedCode] = useState<ItemLibraryItem | null>(null);
  const [amountType, setAmountType] = useState<"number" | "text">("number");
  const [amountText, setAmountText] = useState("");
  const [codeMode, setCodeMode] = useState<"single" | "multiple">("single");
  const [codes, setCodes] = useState<Array<{ item: ItemLibraryItem; operator: "+" | "-" }>>([]);

  // Optional Plot Matrix mapping — same convention as DynamicBillSummaryPanel's
  // modal: a single Valuation Stage dropdown plus Plots/Garages whole-group
  // toggles, off by default per item.
  const [plotMatrixEnabled, setPlotMatrixEnabled] = useState(false);
  const [plotMatrixStage, setPlotMatrixStage] = useState<string | undefined>(undefined);
  const [plotMatrixCategoryName, setPlotMatrixCategoryName] = useState<string | undefined>(undefined);
  const [plotMatrixDistributeTo, setPlotMatrixDistributeTo] = useState<Array<"plots" | "garages">>([]);

  const plotMatrixStageNames = useMemo(() => getPlotMatrixStageNames(plotMatrixColumns), [plotMatrixColumns]);
  const plotMatrixStageColumns = useMemo(
    () => (plotMatrixStage ? findPlotMatrixColumnsForStage(plotMatrixColumns, plotMatrixStage) : []),
    [plotMatrixColumns, plotMatrixStage],
  );

  // Auto-select when a Valuation Stage collapses to just one real column.
  useEffect(() => {
    if (plotMatrixStageColumns.length === 1 && !plotMatrixCategoryName) {
      setPlotMatrixCategoryName(plotMatrixStageColumns[0].categoryName);
    }
  }, [plotMatrixStageColumns, plotMatrixCategoryName]);

  const resetPlotMapping = () => {
    setPlotMatrixEnabled(true);
    setPlotMatrixStage(undefined);
    setPlotMatrixCategoryName(undefined);
    setPlotMatrixDistributeTo([]);
  };

  const buildPlotMapping = (): DynamicBillSummaryPlotMapping | undefined => {
    if (amountType === "text") return undefined;
    if (!plotMatrixEnabled) return undefined;
    if (!plotMatrixStage || !plotMatrixCategoryName || plotMatrixDistributeTo.length === 0) return undefined;
    return { stage: plotMatrixStage, categoryName: plotMatrixCategoryName, distributeTo: plotMatrixDistributeTo };
  };

  // Optional Run Matrix mapping — the Roads & Sewers counterpart of Plot
  // Matrix mapping above: a Section ("tab") dropdown plus a Column ("child")
  // dropdown cascading off it, same shape as the Valuation Stage/Stage
  // cascade. Off by default per item; only surfaced when `enableRunMatrixMapping`.
  const [runMatrixMappingEnabled, setRunMatrixMappingEnabled] = useState(false);
  const [runMatrixSection, setRunMatrixSection] = useState<RunMatrixMappingSectionKey | undefined>(undefined);
  const [runMatrixColumnKey, setRunMatrixColumnKey] = useState<string | undefined>(undefined);

  const runMatrixSectionOptions = useMemo(() => getRunMatrixMappingSectionLabels(), []);
  const runMatrixSectionColumns = useMemo(
    () => findRunMatrixMappingColumnsForSection(runMatrixSection),
    [runMatrixSection],
  );

  // Auto-select when a section collapses to just one column.
  useEffect(() => {
    if (runMatrixSectionColumns.length === 1 && !runMatrixColumnKey) {
      setRunMatrixColumnKey(runMatrixSectionColumns[0].key);
    }
  }, [runMatrixSectionColumns, runMatrixColumnKey]);

  const resetRunMatrixMapping = () => {
    setRunMatrixMappingEnabled(true);
    setRunMatrixSection(undefined);
    setRunMatrixColumnKey(undefined);
  };

  const buildRunMatrixMapping = (): DynamicBillSummaryRunMatrixMapping | undefined => {
    if (amountType === "text") return undefined;
    if (!runMatrixMappingEnabled) return undefined;
    if (!runMatrixSection || !runMatrixColumnKey) return undefined;
    return { section: runMatrixSection, columnKey: runMatrixColumnKey };
  };

  const unitOptions = useMemo(() => {
    const set = new Set<string>(Object.values(UNIT_MAP));
    (itemLibraryData ?? []).forEach((li) => {
      if (li.unit) set.add(formatUnit(li.unit));
    });
    return Array.from(set).sort();
  }, [itemLibraryData]);

  const computeCombinedRate = (entries: Array<{ item: ItemLibraryItem; operator: "+" | "-" }>): number =>
    parseFloat(
      entries
        .reduce((acc, entry, i) => {
          if (i === 0) return entry.item.nettRate;
          return entry.operator === "-" ? acc - entry.item.nettRate : acc + entry.item.nettRate;
        }, 0)
        .toFixed(2),
    );

  const addMultiCode = (li: ItemLibraryItem) => {
    if (codes.some((entry) => entry.item.resourceCode === li.resourceCode)) {
      message.warning(`Code "${li.resourceCode}" has already been added.`);
      return;
    }
    setCodes((prev) => {
      const next = [...prev, { item: li, operator: "+" as const }];
      if (prev.length === 0) setUnit(formatUnit(li.unit) || "");
      return next;
    });
    setSearchText("");
  };
  const removeMultiCode = (index: number) => setCodes((prev) => prev.filter((_, i) => i !== index));
  const setMultiCodeOperator = (index: number, operator: "+" | "-") =>
    setCodes((prev) => prev.map((entry, i) => (i === index ? { ...entry, operator } : entry)));

  const filteredLibraryItems = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!itemLibraryData || !query) return [];
    return itemLibraryData
      .filter(
        (li) => li.resourceDescription?.toLowerCase().includes(query) || li.resourceCode?.toLowerCase().includes(query),
      )
      .slice(0, 30);
  }, [itemLibraryData, searchText]);

  const stubLibraryItem = (resourceCode: string, rate: number, desc: string, itemUnit: string): ItemLibraryItem => ({
    region: "",
    libraryType: "",
    resourceCode,
    resourceDescription: desc,
    unit: itemUnit,
    baseRate: rate,
    nettRate: rate,
    resourceGroup: "",
    resourceType: "",
    costCodeCode: "",
    costCodeDescription: "",
  });

  const resetItemModalFields = () => {
    setDescription("");
    setQty(undefined);
    setUnit("");
    setSearchText("");
    setSelectedCode(null);
    setAmountType("number");
    setAmountText("");
    setCodeMode("single");
    setCodes([]);
    resetPlotMapping();
    resetRunMatrixMapping();
  };

  const openAddItem = (attachKey: string, parentNodeId: string | null) => {
    resetItemModalFields();
    setModalState({ kind: "item", attachKey, parentNodeId, editingNodeId: null });
  };

  const openEditItem = (attachKey: string, node: DynamicBillSummaryItemNode) => {
    resetItemModalFields();
    setDescription(node.description);
    setQty(node.qty);
    setUnit(node.unit ?? "");
    setAmountType(node.amountType === "text" ? "text" : "number");
    setAmountText(node.amountText ?? "");
    if (node.resourceCodes && node.resourceCodes.length > 0) {
      setCodeMode("multiple");
      setCodes(
        node.resourceCodes.map((entry) => ({
          item:
            itemLibraryData?.find((li) => li.resourceCode === entry.resourceCode) ??
            stubLibraryItem(entry.resourceCode, 0, node.description, node.unit ?? ""),
          operator: entry.operator,
        })),
      );
    } else if (node.resourceCode) {
      setCodeMode("single");
      const libraryMatch = itemLibraryData?.find((li) => li.resourceCode === node.resourceCode);
      setSelectedCode(libraryMatch ?? stubLibraryItem(node.resourceCode, node.rate ?? 0, node.description, node.unit ?? ""));
    }
    if (node.plotMapping) {
      setPlotMatrixEnabled(true);
      setPlotMatrixStage(node.plotMapping.stage);
      setPlotMatrixCategoryName(node.plotMapping.categoryName);
      setPlotMatrixDistributeTo(node.plotMapping.distributeTo);
    } else {
      resetPlotMapping();
    }
    if (node.runMatrixMapping) {
      setRunMatrixMappingEnabled(true);
      setRunMatrixSection(node.runMatrixMapping.section as RunMatrixMappingSectionKey);
      setRunMatrixColumnKey(node.runMatrixMapping.columnKey);
    } else {
      resetRunMatrixMapping();
    }
    setModalState({ kind: "item", attachKey, parentNodeId: null, editingNodeId: node.id });
  };

  const openAddSection = (attachKey: string, parentNodeId: string | null) => {
    setSectionLabel("");
    setModalState({ kind: "section", attachKey, parentNodeId, editingNodeId: null });
  };

  const openEditSection = (attachKey: string, node: DynamicBillSummarySectionNode) => {
    setSectionLabel(node.label);
    setModalState({ kind: "section", attachKey, parentNodeId: null, editingNodeId: node.id });
  };

  const closeModal = () => setModalState(null);

  const hasRequiredCode = () => (codeMode === "multiple" ? codes.length > 0 : !!selectedCode);

  const confirmItem = () => {
    if (!modalState || modalState.kind !== "item") return;
    const trimmedDescription = description.trim();
    if (!trimmedDescription) return;
    if (isDuplicateItemDescription(trimmedDescription)) {
      message.warning(`An item named "${trimmedDescription}" already exists in this section.`);
      return;
    }
    if (amountType === "number" && !hasRequiredCode()) return;
    if (amountType === "text" && !amountText.trim()) return;
    if (amountType === "number" && codeMode === "single" && selectedCode && isDuplicateItemCode(selectedCode.resourceCode)) {
      message.warning(`Code "${selectedCode.resourceCode}" already exists in this section.`);
      return;
    }
    const plotMapping = buildPlotMapping();
    const runMatrixMapping = buildRunMatrixMapping();

    const numberFields =
      codeMode === "multiple"
        ? {
            unit: unit.trim() || undefined,
            resourceCode: undefined as string | undefined,
            resourceCodes: codes.map(
              (entry, i): DynamicBillSummaryResourceCodeEntry => ({
                resourceCode: entry.item.resourceCode,
                operator: i === 0 ? "+" : entry.operator,
              }),
            ),
            rate: computeCombinedRate(codes),
          }
        : {
            unit: unit.trim() || undefined,
            resourceCode: selectedCode?.resourceCode,
            resourceCodes: undefined as DynamicBillSummaryResourceCodeEntry[] | undefined,
            rate: selectedCode ? parseFloat(selectedCode.nettRate.toFixed(2)) : undefined,
          };

    if (modalState.editingNodeId) {
      updateNode(
        modalState.attachKey,
        modalState.editingNodeId,
        amountType === "text"
          ? {
              description: trimmedDescription,
              amountType: "text",
              amountText: amountText.trim(),
              unit: undefined,
              qty: undefined,
              rate: undefined,
              resourceCode: undefined,
              resourceCodes: undefined,
              plotMapping: undefined,
              runMatrixMapping: undefined,
            }
          : {
              description: trimmedDescription,
              amountType: "number",
              amountText: undefined,
              qty,
              ...numberFields,
              plotMapping,
              runMatrixMapping,
            },
      );
      closeModal();
      return;
    }

    const newNode: DynamicBillSummaryNode =
      amountType === "text"
        ? {
            id: genExtraNodeId(),
            type: "item",
            description: trimmedDescription,
            amountType: "text",
            amountText: amountText.trim(),
          }
        : {
            id: genExtraNodeId(),
            type: "item",
            description: trimmedDescription,
            amountType: "number",
            qty,
            unit: numberFields.unit,
            rate: numberFields.rate,
            ...(numberFields.resourceCode && { resourceCode: numberFields.resourceCode }),
            ...(numberFields.resourceCodes && { resourceCodes: numberFields.resourceCodes }),
            ...(plotMapping && { plotMapping }),
            ...(runMatrixMapping && { runMatrixMapping }),
          };
    addNode(modalState.attachKey, modalState.parentNodeId, newNode);
    onNodeAdded?.(modalState.parentNodeId ?? (modalState.attachKey !== "__root__" ? modalState.attachKey : null), newNode.id);
    closeModal();
  };

  // The sibling array a new section (or an edited one) actually sits in,
  // within its own attach-point bucket — same convention (and same reason
  // `openEditSection` doesn't track the real parent) as
  // DynamicBillSummaryPanel.tsx's own duplicate-name check.
  const findChildrenOfParent = (nodes: DynamicBillSummaryNode[], parentId: string): DynamicBillSummaryNode[] | null => {
    for (const node of nodes) {
      if (node.id === parentId && isDynamicBillSummarySectionNode(node)) return node.children;
      if (isDynamicBillSummarySectionNode(node)) {
        const found = findChildrenOfParent(node.children, parentId);
        if (found) return found;
      }
    }
    return null;
  };
  const findSiblingsOf = (nodes: DynamicBillSummaryNode[], targetId: string): DynamicBillSummaryNode[] | null => {
    if (nodes.some((n) => n.id === targetId)) return nodes;
    for (const node of nodes) {
      if (isDynamicBillSummarySectionNode(node)) {
        const found = findSiblingsOf(node.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // Case-insensitive, trimmed duplicate-name check against sibling sections
  // only (same parent within the same attach-point bucket, or the bucket's
  // own root) — a section elsewhere in the tree, or in a different attach
  // point, may reuse a name freely.
  const isDuplicateSectionLabel = (label: string): boolean => {
    if (!modalState || modalState.kind !== "section") return false;
    const normalized = label.trim().toLowerCase();
    const bucket = getBucket(modalState.attachKey);
    const siblings = modalState.editingNodeId
      ? findSiblingsOf(bucket, modalState.editingNodeId) ?? bucket
      : modalState.parentNodeId === null
        ? bucket
        : findChildrenOfParent(bucket, modalState.parentNodeId) ?? [];
    return siblings.some(
      (n) =>
        n.id !== modalState.editingNodeId &&
        isDynamicBillSummarySectionNode(n) &&
        n.label.trim().toLowerCase() === normalized,
    );
  };

  // Single-code duplicate check — against sibling items only (same parent
  // section, or the bucket's own root), same scope `isDuplicateSectionLabel`
  // checks names against. A code already used elsewhere in the tree, or
  // combined into another item's "Multiple" list, is unaffected — only a
  // second item in this same section directly linked to the identical single
  // code is blocked, since that would double-count the same rate/cost line.
  const isDuplicateItemCode = (resourceCode: string): boolean => {
    if (!modalState || modalState.kind !== "item") return false;
    const bucket = getBucket(modalState.attachKey);
    const siblings = modalState.editingNodeId
      ? findSiblingsOf(bucket, modalState.editingNodeId) ?? bucket
      : modalState.parentNodeId === null
        ? bucket
        : findChildrenOfParent(bucket, modalState.parentNodeId) ?? [];
    return siblings.some(
      (n) => n.id !== modalState.editingNodeId && !isDynamicBillSummarySectionNode(n) && n.resourceCode === resourceCode,
    );
  };

  // Case-insensitive, trimmed duplicate-name check against sibling items
  // only (same parent section, or the bucket's own root) — same scope
  // `isDuplicateItemCode`/`isDuplicateSectionLabel` check against. An item
  // elsewhere in the tree, or in a different attach point, may reuse a
  // description freely — only two items in this same section sharing one
  // would be confusing/ambiguous about which line a rate/qty belongs to.
  const isDuplicateItemDescription = (value: string): boolean => {
    if (!modalState || modalState.kind !== "item") return false;
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    const bucket = getBucket(modalState.attachKey);
    const siblings = modalState.editingNodeId
      ? findSiblingsOf(bucket, modalState.editingNodeId) ?? bucket
      : modalState.parentNodeId === null
        ? bucket
        : findChildrenOfParent(bucket, modalState.parentNodeId) ?? [];
    return siblings.some(
      (n) =>
        n.id !== modalState.editingNodeId &&
        !isDynamicBillSummarySectionNode(n) &&
        n.description.trim().toLowerCase() === normalized,
    );
  };

  const confirmSection = () => {
    if (!modalState || modalState.kind !== "section") return;
    const label = sectionLabel.trim();
    if (!label) return;
    if (isDuplicateSectionLabel(label)) {
      message.warning(`A section named "${label}" already exists here.`);
      return;
    }
    if (modalState.editingNodeId) {
      updateNode(modalState.attachKey, modalState.editingNodeId, { label });
    } else {
      const newSectionNode: DynamicBillSummaryNode = {
        id: genExtraNodeId(),
        type: "section",
        label,
        children: [],
      };
      addNode(modalState.attachKey, modalState.parentNodeId, newSectionNode);
      onNodeAdded?.(modalState.parentNodeId ?? (modalState.attachKey !== "__root__" ? modalState.attachKey : null), newSectionNode.id);
    }
    closeModal();
  };

  const requestDelete = (attachKey: string, nodeId: string) => setDeleteTarget({ attachKey, nodeId });

  const modals = (
    <>
      <Modal
        title={modalState?.kind === "item" && modalState.editingNodeId ? "Edit Item" : "Add Item"}
        open={modalState?.kind === "item"}
        onCancel={closeModal}
        onOk={confirmItem}
        okText={modalState?.kind === "item" && modalState.editingNodeId ? "Save" : "Add Item"}
        okButtonProps={{
          disabled:
            !description.trim() ||
            isDuplicateItemDescription(description) ||
            (amountType === "number" ? !hasRequiredCode() : !amountText.trim()) ||
            // Plot Matrix Mapping's own fields are marked required (*) for
            // Number-type items — enforce that here too, not just visually,
            // same as Description/Item Library Code above. Number-type only
            // (matches buildPlotMapping's own amountType guard) — Text-type
            // items never show these fields, so plotMatrixEnabled being left
            // over from the last Number-type item modal must not block them.
            // Gated on `plotMatrixColumns.length > 0` (same guard the section's
            // own render condition below uses) — `plotMatrixEnabled` defaults to
            // `true` on every Add Item open (resetPlotMapping), regardless of
            // whether this page even passed real Plot Matrix columns through
            // (e.g. the Roads & Sewers fixed Bill Summaries, which have no Plot
            // Matrix concept at all and never pass plotMatrixColumns). Without
            // this guard, a page with no Plot Matrix columns permanently
            // disabled Add/Save — the required Stage/Category/Distribute-to
            // fields could never be filled in because the section that holds
            // them was never on screen.
            (amountType === "number" &&
              plotMatrixColumns.length > 0 &&
              plotMatrixEnabled &&
              (!plotMatrixStage || !plotMatrixCategoryName || plotMatrixDistributeTo.length === 0)) ||
            // Run Matrix Mapping's own fields — same required-when-enabled
            // enforcement and the same "gated on the flag that also gates the
            // section's own render condition" reasoning as Plot Matrix Mapping
            // immediately above (e.g. /groundworks/review never passes
            // enableRunMatrixMapping, so runMatrixMappingEnabled defaulting to
            // true must not block Add/Save there).
            (amountType === "number" &&
              enableRunMatrixMapping &&
              runMatrixMappingEnabled &&
              (!runMatrixSection || !runMatrixColumnKey)),
        }}
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>
            Amount Type
          </Text>
          <Segmented
            value={amountType}
            onChange={(value) => setAmountType(value as "number" | "text")}
            options={[
              { label: "Number", value: "number" },
              { label: "Text", value: "text" },
            ]}
          />
        </div>

        {amountType === "text" ? (
          <>
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>
              Description <Text type="danger">*</Text>
            </Text>
            <TextInput
              placeholder="Item description"
              value={description}
              onChange={setDescription}
              maxLength={100}
              status={isDuplicateItemDescription(description) ? "error" : undefined}
            />
            {isDuplicateItemDescription(description) && (
              <Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
                An item with this name already exists in this section.
              </Text>
            )}
          </div>
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>
              Amount (Text)
            </Text>
            <TextInput placeholder="e.g. Included, Rate Only" value={amountText} onChange={setAmountText} textOnly maxLength={100} />
          </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ display: "block", marginBottom: 4 }}>
                Item Library Code <Text type="danger">*</Text>
              </Text>
              <Segmented
                value={codeMode}
                onChange={(value) => {
                  setCodeMode(value as "single" | "multiple");
                  setSearchText("");
                }}
                options={[
                  { label: "Single", value: "single" },
                  { label: "Multiple", value: "multiple" },
                ]}
                size="small"
                style={{ marginBottom: 8 }}
              />

              {codeMode === "single" ? (
                selectedCode ? (
                  <Tag
                    closable
                    closeIcon={<CloseCircleFilled />}
                    onClose={() => setSelectedCode(null)}
                    color="blue"
                    style={{ padding: "4px 8px", fontSize: 12 }}
                  >
                    {selectedCode.resourceCode} — {selectedCode.resourceDescription} (£
                    {selectedCode.nettRate.toFixed(2)})
                  </Tag>
                ) : (
                  <>
                    <Input.Search
                      placeholder="Search by description or code"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      autoFocus
                    />
                    {searchText.trim() && (
                      <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0" }}>
                        {filteredLibraryItems.length === 0 ? (
                          <div style={{ padding: 10 }}>
                            <Text type="secondary">No matches — refine your search.</Text>
                          </div>
                        ) : (
                          filteredLibraryItems.map((li) => (
                            <div
                              key={li.resourceCode}
                              onClick={() => {
                                if (isDuplicateItemCode(li.resourceCode)) {
                                  message.warning(`Code "${li.resourceCode}" already exists in this section.`);
                                  return;
                                }
                                setSelectedCode(li);
                                setDescription(li.resourceDescription);
                                setUnit(formatUnit(li.unit) || "");
                              }}
                              style={{ padding: "8px 10px", borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
                            >
                              <div style={{ fontWeight: 500, fontSize: 13 }}>{li.resourceDescription}</div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                {li.resourceCode} · {li.unit} · £{li.nettRate.toFixed(2)}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )
              ) : (
                <div>
                  {codes.map((entry, i) => (
                    <div key={`${entry.item.resourceCode}_${i}`} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      {i > 0 && (
                        <Select
                          value={entry.operator}
                          onChange={(value) => setMultiCodeOperator(i, value as "+" | "-")}
                          options={RESOURCE_CODE_OPERATOR_OPTIONS}
                          size="small"
                          style={{ width: 56 }}
                        />
                      )}
                      <Tag
                        closable
                        closeIcon={<CloseCircleFilled />}
                        onClose={() => removeMultiCode(i)}
                        color="blue"
                        style={{ padding: "4px 8px", fontSize: 12 }}
                      >
                        {entry.item.resourceCode} — {entry.item.resourceDescription} (£{entry.item.nettRate.toFixed(2)})
                      </Tag>
                    </div>
                  ))}
                  <Input.Search
                    placeholder="Search by description or code to add"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  {searchText.trim() && (
                    <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0" }}>
                      {filteredLibraryItems.length === 0 ? (
                        <div style={{ padding: 10 }}>
                          <Text type="secondary">No matches — refine your search.</Text>
                        </div>
                      ) : (
                        filteredLibraryItems.map((li) => (
                          <div
                            key={li.resourceCode}
                            onClick={() => addMultiCode(li)}
                            style={{ padding: "8px 10px", borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
                          >
                            <div style={{ fontWeight: 500, fontSize: 13 }}>{li.resourceDescription}</div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                              {li.resourceCode} · {li.unit} · £{li.nettRate.toFixed(2)}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {codes.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text strong>Combined Rate: </Text>
                      <Text>£{computeCombinedRate(codes).toFixed(2)}</Text>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ display: "block", marginBottom: 4 }}>
                Description <Text type="danger">*</Text>
              </Text>
              <TextInput
                placeholder="Item description"
                value={description}
                onChange={setDescription}
                maxLength={100}
                status={isDuplicateItemDescription(description) ? "error" : undefined}
              />
              {isDuplicateItemDescription(description) && (
                <Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
                  An item with this name already exists in this section.
                </Text>
              )}
            </div>

            <div>
              <Text strong style={{ display: "block", marginBottom: 4 }}>
                Unit
              </Text>
              <Select
                value={unit || undefined}
                onChange={(value) => setUnit(value)}
                options={unitOptions.map((u) => ({ value: u, label: u }))}
                placeholder="Select a unit"
                showSearch
                allowClear
                onClear={() => setUnit("")}
                style={{ width: "100%" }}
              />
            </div>

            {plotMatrixColumns.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Divider style={{ margin: "0 0 12px" }}>Plot Matrix Mapping</Divider>
                {plotMatrixEnabled && (
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 4 }}>
                      Valuation Stage <Text type="danger">*</Text>
                    </Text>
                    <Select
                      value={plotMatrixStage}
                      onChange={(value) => {
                        setPlotMatrixStage(value);
                        setPlotMatrixCategoryName(undefined);
                        if (!isGarageEligibleValuationStage(value)) {
                          setPlotMatrixDistributeTo((prev) => prev.filter((v) => v !== "garages"));
                        }
                      }}
                      options={plotMatrixStageNames.map((stage) => ({ value: stage, label: stage }))}
                      placeholder="Select a valuation stage"
                      showSearch
                      style={{ width: "100%", marginBottom: 12 }}
                    />

                    <Text strong style={{ display: "block", marginBottom: 4 }}>
                      Stage <Text type="danger">*</Text>
                    </Text>
                    <Select
                      value={plotMatrixCategoryName}
                      onChange={setPlotMatrixCategoryName}
                      options={plotMatrixStageColumns.map((c) => ({
                        value: c.categoryName,
                        label: getPlotMatrixSubStageLabel(c.categoryName),
                      }))}
                      placeholder={plotMatrixStage ? "Select a stage" : "Select a valuation stage first"}
                      disabled={!plotMatrixStage}
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%", marginBottom: 12 }}
                    />

                    <Text strong style={{ display: "block", marginBottom: 4 }}>
                      Distribute amount across <Text type="danger">*</Text>
                    </Text>
                    <Space>
                      <Checkbox
                        checked={plotMatrixDistributeTo.includes("plots")}
                        onChange={(e) =>
                          setPlotMatrixDistributeTo((prev) =>
                            e.target.checked ? [...prev, "plots"] : prev.filter((v) => v !== "plots"),
                          )
                        }
                      >
                        Plots
                      </Checkbox>
                      {(!plotMatrixStage || isGarageEligibleValuationStage(plotMatrixStage)) && (
                        <Checkbox
                          checked={plotMatrixDistributeTo.includes("garages")}
                          onChange={(e) =>
                            setPlotMatrixDistributeTo((prev) =>
                              e.target.checked ? [...prev, "garages"] : prev.filter((v) => v !== "garages"),
                            )
                          }
                        >
                          Garages
                        </Checkbox>
                      )}
                    </Space>
                  </div>
                )}
              </div>
            )}

            {enableRunMatrixMapping && (
              <div style={{ marginTop: 16 }}>
                <Divider style={{ margin: "0 0 12px" }}>Run Matrix Mapping</Divider>
                {runMatrixMappingEnabled && (
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 4 }}>
                      Section <Text type="danger">*</Text>
                    </Text>
                    <Select
                      value={runMatrixSection}
                      onChange={(value) => {
                        setRunMatrixSection(value as RunMatrixMappingSectionKey);
                        setRunMatrixColumnKey(undefined);
                      }}
                      options={runMatrixSectionOptions.map((s) => ({ value: s.key, label: s.label }))}
                      placeholder="Select a Run Matrix section"
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%", marginBottom: 12 }}
                    />

                    <Text strong style={{ display: "block", marginBottom: 4 }}>
                      Column <Text type="danger">*</Text>
                    </Text>
                    <Select
                      value={runMatrixColumnKey}
                      onChange={setRunMatrixColumnKey}
                      options={runMatrixSectionColumns.map((c) => ({ value: c.key, label: c.label }))}
                      placeholder={runMatrixSection ? "Select a column" : "Select a section first"}
                      disabled={!runMatrixSection}
                      showSearch
                      optionFilterProp="label"
                      style={{ width: "100%" }}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal>

      <Modal
        title={modalState?.kind === "section" && modalState.editingNodeId ? "Edit Section" : "Add Section"}
        open={modalState?.kind === "section"}
        onCancel={closeModal}
        onOk={confirmSection}
        okText={modalState?.kind === "section" && modalState.editingNodeId ? "Save" : "Add Section"}
        okButtonProps={{ disabled: !sectionLabel.trim() || isDuplicateSectionLabel(sectionLabel) }}
        destroyOnClose
      >
        <Text strong style={{ display: "block", marginBottom: 4 }}>
          Section Name
        </Text>
        <TextInput
          placeholder="Section name"
          value={sectionLabel}
          onChange={setSectionLabel}
          onPressEnter={confirmSection}
          maxLength={100}
          status={isDuplicateSectionLabel(sectionLabel) ? "error" : undefined}
          autoFocus
        />
        {isDuplicateSectionLabel(sectionLabel) && (
          <Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
            A section with this name already exists here.
          </Text>
        )}
      </Modal>

      <DeleteConfirmationModal
        visible={deleteTarget !== null}
        onConfirm={() => {
          if (deleteTarget) removeNode(deleteTarget.attachKey, deleteTarget.nodeId);
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
        title="Delete this row and its contents?"
      />
    </>
  );

  return {
    getBucket,
    updateNode,
    unitOptions,
    openAddItem,
    openEditItem,
    openAddSection,
    openEditSection,
    requestDelete,
    modals,
  };
}
